import React, {useState, useEffect, useRef} from 'react';
import Button from '../Button/Button';
import ButtonRow from '../Button/ButtonRow';
import { useMessageLibrary, useRefineMessage, useSuggestThreadReplies } from '../../Data/Thread';
import Dropdown from '../Dropdown';
import { useSession } from '../Session';
import { useDialogues } from '../Dialogues';
import SavedReplies from './SavedReplies';

const Reply = ({saving, onReply, thread}) => {
    const {hasFeature} = useSession();
    const {featureUnavailable} = useDialogues();
    const [reply, setReply] = useState('');
    const [textAreaHeight, setTextAreaHeight] = useState('50px');
    const {refine, isRefining} = useRefineMessage(thread.id);
    const fieldRef = useRef();

    useEffect(() => {
        if (!fieldRef?.current ?? null) {
            return;
        }
        setTextAreaHeight(Math.max(70, fieldRef.current.scrollHeight) + 'px');
    }, [reply])

    const handleSubmit = async (e) => {
        e && e.preventDefault();
        await onReply(reply);
        setReply('');
    }

    const refineMessage = async (action) => {
        try {
            const refined = await refine(reply, action);
            setReply(refined);
        } catch (e) {
            // Toast is shown by the hook
            console.error(e);
        }
    }

    return (
        <div className="flex items-start space-x-4">
            <div className="min-w-0 flex-1">
                <form className="relative" onSubmit={handleSubmit}>
                    <div className="overflow-hidden rounded-lg shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-brand">
                        <label htmlFor="comment" className="sr-only">
                            Reply...
                        </label>
                        <textarea
                            ref={fieldRef}
                            rows={2}
                            name="comment"
                            id="comment"
                            style={{height: textAreaHeight}}
                            className="block w-full resize-none border-0 bg-transparent py-1.5 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                            placeholder="Reply..."
                            value={reply}
                            onChange={(e) => setReply(e.target.value)}
                        />

                        {/* Spacer element to match the height of the toolbar */}
                        <div className="py-2" aria-hidden="true">
                            {/* Matches height of button in toolbar (1px border + 36px content height) */}
                            <div className="py-px">
                                <div className="h-9" />
                            </div>
                        </div>
                    </div>

                    <div className="absolute inset-x-0 bottom-0 flex justify-between py-2 pl-3 pr-2">
                        <div className="flex items-center space-x-5">
                            <div className="flex items-center">
                               {thread.entityModel && <SavedMessages {...{thread, reply, setReply, isRefining, hasFeature, featureUnavailable}} />}
                            </div>
                            <div className="flex items-center">
                                {/*  */}
                            </div>
                        </div>
                        <div className="shrink-0">
                            <ButtonRow>
                                {thread.entityModel && <AIActions {...{thread, refineMessage, isRefining, hasFeature, featureUnavailable}} />}

                                <Button theme="primary"
                                    loading={saving}
                                    disabled={saving || isRefining}
                                    label="Reply"
                                    type="submit"
                                    onClick={handleSubmit}
                                    zeroMargin
                                />
                            </ButtonRow>
                            {/* <button
                                type="submit"
                                className="btn-primary inline-flex items-center rounded-md px-3 py-2 text-sm font-semibold shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
                                disabled={saving}
                            >
                                {saving && <Loading className="mr-2" inline bg="dark" />}
                                Reply
                            </button> */}
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
}

const AIActions = ({thread, refineMessage, isRefining, hasFeature, featureUnavailable}) => {
    const actions = {
        'Quotes/Quote': [
            {
                title: 'Correct grammar',
                action: 'refine-quote-message'
            },
            {
                title: 'Make more formal',
                action: 'formalise-quote-message'
            },
            {
                title: 'Expand from prompt',
                action: 'expand-quote-message'
            }
        ],
        'Customers/Customer': [
            {
                title: 'Correct grammar',
                action: 'refine-customer-message'
            },
            {
                title: 'Make more formal',
                action: 'formalise-customer-message'
            },
            {
                title: 'Expand from prompt',
                action: 'expand-customer-message'
            }
        ],
    }
    if (!thread || !actions[thread.entityModel]) {
        return null;
    }

    if (!hasFeature('AI_ASSISTANT')) {
        return <Button
            theme="light"
            icon="WandMagicSparkles"
            label="AI"
            onClick={() => featureUnavailable('AI_ASSISTANT')}
            zeroMargin
        />
    }

    return <Dropdown
        icon="WandMagicSparkles"
        label="AI"
        options={
            actions[thread.entityModel].map(action => ({
                title: action.title,
                onClick: () => refineMessage(action.action)
            }))
        }
        disabled={isRefining}
        loading={isRefining}
        openUp
    />
}

const SavedMessages = ({thread, reply, setReply, isRefining, hasFeature, featureUnavailable}) => {
    if (!hasFeature('SAVED_REPLIES')) {
        return <Button
            theme="light"
            icon="Book"
            label="Saved"
            onClick={() => featureUnavailable('SAVED_REPLIES')}
            zeroMargin
        />
    }

    if (!thread) {
        return null;
    }


    // Do this separately so we can conditionally use the hook
    return <TheSavedMessages {...{thread, reply, setReply, isRefining}} />;
}

const TheSavedMessages = ({thread, reply, setReply, isRefining}) => {
    const {replies, refresh} = useSuggestThreadReplies(thread.id);
    const {createMessage, isSaving} = useMessageLibrary(thread.entityModel);
    const [managingSaved, setManagingSaved] = useState(false);

    let options = [{label: 'Loading...', disabled: true}];

    if (replies) {
        options = replies.map(reply => ({
            title: reply.title,
            onClick: () => setReply(reply.content)
        }));
        options = [
            {
                title: 'Save reply',
                icon: 'Plus',
                disabled: !reply,
                onClick: () => createMessage(reply).then(refresh)
            },
            {
                title: 'Manage saved',
                icon: 'Book',
                onClick: () => setManagingSaved(true)
            },
            {div: true},
        ].concat(options);
    }

    return <>
        <Dropdown
            icon="Book"
            label="Saved"
            options={options}
            loading={isSaving}
            disabled={isSaving || isRefining}
            openUp
            openRight
        />
        <SavedReplies entityModel={thread.entityModel}
            isOpen={managingSaved}
            setIsOpen={setManagingSaved}
            onChanged={refresh}
        />
    </>
}

export default Reply;
